/**
 * Reusable stateless form component for Scene
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

// import third-party libraries
import { EditorState, convertToRaw } from 'draft-js';

// import form components
import {
  CdnPathInput
  , CheckboxInput
  , RadioInput
  , RichEditor
  , TextAreaInput
  , TextInput
} from '../../../global/components/forms';

// import module components
import VideoTrackListItem from './VideoTrackListItem.js.jsx';

function SceneForm({
  addSceneTrack
  , cancelLink
  , courseList
  , deleteTrack
  , formHelpers
  , formTitle
  , formType
  , handleFormChange
  , handleFormSubmit
  , handleTrackFormChange
  , scene
  , contentModuleList
  , warningText
}) {

  // set the button text
  const buttonText = formType === "create" ? "Create Scene" : "Update Scene";

  // set the form header
  const header = formTitle ? <div className="formHeader"><h2> {formTitle} </h2><hr/></div> : <div/>;
  const warning = warningText ? <div style={{color: 'red'}}><h3>{warningText}</h3></div> : <div/>;

  // build links
  const courseLinks = courseList ?
    (courseList.map((course, i) => {
        return (
          <Link to={`/admin/authoring/courses/${course._id}`} target={'_blank'} key={course._id + i}>
            <p>{`Course: ${course.title} `}<i className="fa fa-external-link"/></p>
          </Link>
        )
      })
    )
  :
    null

  const cmLinks = contentModuleList ?
    (contentModuleList.map((cm, i) => {
        return (
          <Link to={`/admin/authoring/content-modules/${cm._id}`} target={'_blank'} key={cm._id + i}>
            <p>{`Content Module: ${cm.title} `}<i className="fa fa-external-link"/></p>
          </Link>
        )
      })
    )
  :
    null

  return (
    <div className="yt-container">
      <div className="yt-row center-horiz">
        <div className="form-container -slim">
          <form name="scene.sceneForm" className="scene-form" onSubmit={handleFormSubmit}>
            {header}
            {warning}
            {courseLinks}
            {cmLinks}
            <div className="-form-step"><span className="-num">1</span> General info</div>
            <div className="yt-col full m_60">
              <TextInput
                change={handleFormChange}
                label="Title"
                name="scene.title"
                required={true}
                value={scene.title}
              />
              <TextAreaInput
                cols={100}
                change={handleFormChange}
                helpText={<span>{scene.summary.length} of 180 </span>}
                label="Scene summary"
                maxLength="180"
                name="scene.summary"
                required={false}
                rows="3"
                value={scene.summary}
              />
              <RadioInput
                helpText={formHelpers.hasMediaType === '' ? "'Neither' means we need to be sure to build a custom follow up question" : null}
                label="Does this scene have a video or PDF?"
                options={[
                  {val: 'video', display: 'Video'},
                  {val: 'pdf', display: 'PDF'},
                  {val: '', display: 'Neither'},
                ]}
                name="formHelpers.hasMediaType"
                value={formHelpers.hasMediaType}
                change={handleFormChange}
                inLine={true}
              />
              { formHelpers.hasMediaType === 'video' ?
                <div>
                  <CheckboxInput
                    change={handleFormChange}
                    value={scene.transparentOverlay}
                    label={"Transparent Overlay"}
                    name={"scene.transparentOverlay"}
                    helpText={"If checked, will show an overlay over top of the video's poster url or last frame. Otherwise default to white background."}
                  />
                  <CdnPathInput
                    allowType="video"
                    change={handleFormChange}
                    label="Video Path"
                    name="scene.videoPath"
                    required={false}
                    validate={false}
                    value={scene.videoPath}
                  />
                  <CdnPathInput
                    change={handleFormChange}
                    helpText={<span><strong>NOTE:</strong> this is only for displaying static image at the end of the video. Will use default if left blank.</span>}
                    label="Video paused poster Path"
                    name="scene.posterPath"
                    required={false}
                    validate={false}
                    value={scene.posterPath}
                  />
                </div>
                :
                formHelpers.hasMediaType === 'pdf' ?
                <CdnPathInput
                  allowType="document"
                  change={handleFormChange}
                  label="PDF Path"
                  name="scene.pdfPath"
                  required={false}
                  type='document'
                  validate={false}
                  value={scene.pdfPath}
                />
                :
                null
              }
            </div>
            <div className="-form-step"><span className="-num">2</span> Add overlay text and/or a custom follow up question</div>
            <p><em>Will default to "Please make your selection from the action button(s) below." if left blank</em></p>
            <div className="yt-col full m_60">
              <TextAreaInput
                cols={100}
                change={handleFormChange}
                helpText={<div className="yt-row space-between"><span>This will show up on the left of the follow-up nodes</span><span>{scene.overlayText.length} of 300 </span></div>}
                label="Left-hand overlay text"
                maxLength="300"
                name="scene.overlayText"
                required={false}
                rows="3"
                value={scene.overlayText}
              />
            </div>
            <div className="yt-col full m_60">
              <TextAreaInput
                cols={100}
                change={handleFormChange}
                helpText={<span>{scene.followupQuestion.length} of 180 </span>}
                label="Followup question"
                maxLength="180"
                name="scene.followupQuestion"
                required={false}
                rows="3"
                value={scene.followupQuestion}
              />
            </div>
            <div className="-form-step"><span className="-num">3</span> Add a chart</div>
            <p><em>This shows up below the video. </em></p>
            <RichEditor
              change={handleFormChange}
              isNewContent={formHelpers.isNewChartContent}
              label="Chart for reference"
              name="scene.chart"
              required={false}
              value={scene.chart && scene.chart.blocks && scene.chart.blocks.length > 0 ? scene.chart : convertToRaw(EditorState.createEmpty().getCurrentContent())}
            />
            <div className="-form-step"><span className="-num">4</span> Add a detailed scene description</div>
            <p><em>This shows up below the video. Can be thought of as the accompanying article to the video.</em></p>
            <RichEditor
              change={handleFormChange}
              isNewContent={formHelpers.isNewDescriptionContent}
              label="Detailed scene description"
              name="scene.description"
              required={false}
              value={scene.description && scene.description.blocks && scene.description.blocks.length > 0 ? scene.description : convertToRaw(EditorState.createEmpty().getCurrentContent())}
            />
            <div className="-form-step"><span className="-num">5</span>Script</div>
            {/* <p><em>TODO: Add some copy here to help authoring out</em></p> */}
            <div className="yt-col full m_60">
              <TextAreaInput
                cols={100}
                change={handleFormChange}
                label="Script"
                maxLength="300"
                name="scene.script"
                required={false}
                rows="3"
                value={scene.script}
              />
            </div>
          <div className="-form-step"><span className="-num">6</span> Transcript & subtitles</div>
            <RichEditor
              change={handleFormChange}
              isNewContent={formHelpers.isNewTranscriptContent}
              label="Transcript for reference"
              name="scene.transcript"
              required={false}
              value={scene.transcript && scene.transcript.blocks && scene.transcript.blocks.length > 0 ? scene.transcript : convertToRaw(EditorState.createEmpty().getCurrentContent())}
            />
            <div className="input-group">
              <label>Subtitle Tracks</label>
              <div className="-subtitle-tracks">
                { scene.tracks.map((track, i) =>
                  <VideoTrackListItem
                    deleteTrack={deleteTrack}
                    handleTrackFormChange={handleTrackFormChange}
                    index={i}
                    key={i}
                    track={track}
                  />
                )}
              </div>
              <button type="button" className="yt-btn x-small link success" onClick={addSceneTrack} style={{marginRight:20}}>+ Add track file</button>
            </div>
            <div className="input-group">
              <div className="yt-row space-between">
                <Link className="yt-btn link" to={cancelLink}>Cancel</Link>
                <button className="yt-btn " type="submit" > {buttonText} </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

SceneForm.propTypes = {
  addSceneTrack: PropTypes.func.isRequired
  , cancelLink: PropTypes.string.isRequired
  , deleteTrack: PropTypes.func.isRequired
  , formHelpers: PropTypes.object.isRequired
  , formTitle: PropTypes.string
  , formType: PropTypes.string.isRequired
  , handleFormChange: PropTypes.func.isRequired
  , handleFormSubmit: PropTypes.func.isRequired
  , handleTrackFormChange: PropTypes.func.isRequired
  , scene: PropTypes.object.isRequired
}

SceneForm.defaultProps = {
  formTitle: ''
}

export default SceneForm;



// WEBPACK FOOTER //
// modules/scene/components/SceneForm.js.jsx